import { useEffect } from 'react';
import type { OverlayScope } from './context';

/**
 * 焦点守卫计数。只在 `default` 作用域生效。
 */
let guardCount = 0;
let observer: MutationObserver | null = null;
let originalDescriptor: PropertyDescriptor | null = null;

/** 用户「正在输入」的控件。焦点落在这些标签上时，绝不允许被抢走。 */
const TYPING_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * 是否允许这次 `focus()` 调用。
 *
 * 判定核心：**决不允许**把焦点从用户手里抢走。
 * 之所以要拦，是因为纯鼠标点击弹窗关闭按钮也会给它焦点，
 * 而输入框所在的列表每次刷新都会重挂载，重挂载产生的副作用式 `focus()`
 * 会把焦点从用户正在输入的输入框里拽到那个按钮上，
 * 表现就是「每输入一个字符焦点就跳走」。
 *
 * 允许的情形只有两类：
 *   1. 目标本身就是输入控件 —— 用户显式点某个输入框，
 *      或弹窗打开时把首焦点放进输入框，这是全站唯一需要主动转移焦点的地方；
 *   2. 焦点还没归属（`null` / `body` / `documentElement`）——
 *      例如页面刚加载、顶层弹窗刚挂载时需要设置首焦点。
 *
 * 其余情况默认放行（不干预键盘用户与既有焦点管理），
 * 但**「用户正在输入框里打字」是唯一的硬拦截条件**。
 */
export function allowsAutoFocus(
  element: Element,
  active: Element | null,
  body?: Element | null,
  root?: Element | null,
): boolean {
  // 目标就是输入控件：显式聚焦输入框永远允许。
  if (TYPING_TAGS.has(element.tagName)) return true;

  // 焦点还没归属（页面刚加载 / 弹窗刚挂载）：允许设置首焦点。
  const ownerDocument = element.ownerDocument ?? null;
  const idleTargets = [body ?? ownerDocument?.body ?? null, root ?? ownerDocument?.documentElement ?? null];
  if (!active || idleTargets.includes(active)) return true;

  // 用户正在输入框里打字：任何人不得把焦点抢走。
  if (TYPING_TAGS.has(active.tagName)) return false;

  return true;
}

function shouldBlock(element: Element): boolean {
  return !allowsAutoFocus(element, document.activeElement, document.body, document.documentElement);
}

function ensureObserver(): void {
  // 弹窗很可能在打开前就把按钮「关掉」了，因此监听不依赖任何嵌套的浮层宿主：
  // 应用里任何一次 render 都会产生 childList 变更，检查足够及时。
  observer = new MutationObserver(() => {
    if (guardCount === 0) return;
    if (!shouldBlock(document.body)) return;
    queueMicrotask(() => {
      // 等这一轮 React 提交全部结束，否则 focus() 会把还没挂载的节点顶掉
      if (guardCount === 0) return;
      if (!shouldBlock(document.body)) return;
      document.body.focus();
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function install(): void {
  guardCount += 1;
  if (guardCount > 1) return;

  if (typeof HTMLElement !== 'undefined') {
    originalDescriptor = Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'focus') ?? null;
    if (originalDescriptor) {
      Object.defineProperty(HTMLElement.prototype, 'focus', {
        ...originalDescriptor,
        value(this: HTMLElement, options?: FocusOptions) {
          if (shouldBlock(this)) return;
          originalDescriptor!.value!.call(this, options);
        },
      });
    }
  }

  ensureObserver();
}

function uninstall(): void {
  guardCount -= 1;
  if (guardCount > 0) return;

  observer?.disconnect();
  observer = null;

  if (originalDescriptor) {
    Object.defineProperty(HTMLElement.prototype, 'focus', originalDescriptor);
    originalDescriptor = null;
  }
}

/**
 * 焦点守卫。
 *
 * 背景：一个纯鼠标操作（点关闭按钮 ×）也会触发按钮的聚焦；
 * Chromium 的「空间导航」在关闭后把焦点落到最靠近的按钮上，
 * Safari 则一律回到 `body`。此时若页面因定时刷新发生重挂载，
 * 重挂载产生的 `focus()` 会把焦点从用户正在使用的输入框里拽到那个按钮上，
 * 表现就是「每输入一个字符焦点就跳走」。
 *
 * 既然站点并没有「按节流重挂载输入框」的需求，这里就直接把这类
 * 非预期的 `focus()` 拦掉；同时用 MutationObserver 在 React 提交结束时
 * 检查焦点是否掉在 `body` 之外，把焦点拉回中性的 `body`。
 *
 * 只在 `default` 作用域启用：单机管理页是硬实时界面，
 * 不做任何可能干扰操作的焦点干预，也避免空跑一个 DOM 观察者。
 */
export function useFocusGuard(scope: OverlayScope = 'default'): void {
  useEffect(() => {
    if (scope !== 'default') return;
    install();
    return uninstall;
  }, [scope]);
}
